import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../contexts/ThemeContext";
import { parseWebsiteContent, validateOpportunityData, convertToDatabaseFormat, ParsedOpportunity } from "../services/aiImportService";
import { createOrganizationOpportunity } from "../services/parseService";
import { getTestContent } from "../utils/aiImportTestData";
import { OpportunityType, GradeLevel, InteractionType } from "../types";
import OrganizationOpportunityForm from "./OrganizationOpportunityForm";

type ImportType = 'single' | 'multiple';

interface AIImportModalProps {
  onClose: () => void;
  onSuccess: () => void;
  organizationUserId: string;
  organizationName: string;
}

const AIImportModal: React.FC<AIImportModalProps> = ({
  onClose,
  onSuccess,
  organizationUserId,
  organizationName,
}) => {
  const { theme } = useTheme();
  
  // State management
  const [currentStep, setCurrentStep] = useState<'setup' | 'processing' | 'review' | 'complete'>('setup');
  const [importType, setImportType] = useState<ImportType>('multiple');
  const [content, setContent] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedOpportunities, setParsedOpportunities] = useState<ParsedOpportunity[]>([]);
  const [selectedOpportunities, setSelectedOpportunities] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [processingStats, setProcessingStats] = useState<{
    confidence: number;
    processingTime: number;
  } | null>(null);
  const [editingOpportunity, setEditingOpportunity] = useState<number | null>(null);
  const [showOpportunityForm, setShowOpportunityForm] = useState(false);
  const [editingOpportunityData, setEditingOpportunityData] = useState<any>(null);

  const handleParseContent = async () => {
    if (!content.trim()) {
      const message = "Please enter website content to parse.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Missing Content", message);
      }
      return;
    }

    try {
      setIsProcessing(true);
      setCurrentStep('processing');
      setParsedOpportunities([]);
      setSelectedOpportunities(new Set());
      setProcessingStats(null);

      console.log('🤖 Starting AI content parsing...');
      const result = await parseWebsiteContent(content, organizationName, importType);

      if (result.opportunities.length === 0) {
        const message = "No opportunities were found in the provided content. Please try with different content.";
        if (Platform.OS === 'web') {
          alert(message);
        } else {
          Alert.alert("No Opportunities Found", message);
        }
        setCurrentStep('setup');
        return;
      }

      setParsedOpportunities(result.opportunities);
      setProcessingStats({
        confidence: result.confidence,
        processingTime: result.processingTime
      });

      // Auto-select all opportunities by default
      const allIndices = new Set(result.opportunities.map((_, index) => index));
      setSelectedOpportunities(allIndices);
      setCurrentStep('review');

      console.log(`✅ Found ${result.opportunities.length} opportunities`);

    } catch (error) {
      console.error('❌ AI parsing failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to parse content';
      const fullMessage = `AI parsing failed: ${errorMessage}`;
      
      if (Platform.OS === 'web') {
        alert(fullMessage);
      } else {
        Alert.alert("Parsing Error", fullMessage);
      }
      setCurrentStep('setup');
    } finally {
      setIsProcessing(false);
    }
  };

  const validateOpportunity = (opportunity: ParsedOpportunity): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!opportunity.title?.trim()) {
      errors.push('Title is required');
    }
    
    if (!opportunity.description?.trim()) {
      errors.push('Description is required');
    }
    
    if (!opportunity.organization?.trim()) {
      errors.push('Organization is required');
    }
    
    if (!opportunity.location?.trim()) {
      errors.push('Location is required');
    }
    
    if (!opportunity.website?.trim()) {
      errors.push('Website is required');
    }
    
    const types = Array.isArray(opportunity.type) ? opportunity.type : [opportunity.type];
    if (types.length === 0 || !types[0]?.trim()) {
      errors.push('At least one opportunity type is required');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  };

  const handleSaveOpportunities = async () => {
    if (selectedOpportunities.size === 0) {
      const message = "Please select at least one opportunity to save.";
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("No Selection", message);
      }
      return;
    }

    // Validate all selected opportunities
    const validationErrors: { [key: number]: string[] } = {};
    let hasErrors = false;

    for (const index of selectedOpportunities) {
      const opportunity = parsedOpportunities[index];
      const validation = validateOpportunity(opportunity);
      if (!validation.valid) {
        validationErrors[index] = validation.errors;
        hasErrors = true;
      }
    }

    if (hasErrors) {
      const errorMessage = "Some opportunities have validation errors. Please fix them before saving:\n\n" +
        Object.entries(validationErrors)
          .map(([index, errors]) => `Opportunity ${parseInt(index) + 1}: ${errors.join(', ')}`)
          .join('\n');
      
      if (Platform.OS === 'web') {
        alert(errorMessage);
      } else {
        Alert.alert("Validation Errors", errorMessage);
      }
      return;
    }

    try {
      setIsSaving(true);
      let savedCount = 0;
      let errorCount = 0;

      // Process selected opportunities
      for (const index of selectedOpportunities) {
        const opportunity = parsedOpportunities[index];
        
        // Validate opportunity data
        const validation = validateOpportunityData(opportunity);
        if (!validation.valid) {
          console.warn(`Skipping opportunity ${index + 1}: ${validation.errors.join(', ')}`);
          errorCount++;
          continue;
        }

        try {
          // Convert to database format
          const dbFormat = convertToDatabaseFormat(opportunity, organizationUserId);
          
          // Save to database
          await createOrganizationOpportunity(dbFormat);
          savedCount++;
          
          console.log(`✅ Saved opportunity: ${opportunity.title}`);
        } catch (saveError) {
          console.error(`❌ Failed to save opportunity ${index + 1}:`, saveError);
          errorCount++;
        }
      }

      // Show results
      const message = `Successfully saved ${savedCount} opportunities${errorCount > 0 ? ` (${errorCount} failed)` : ''}.`;
      if (Platform.OS === 'web') {
        alert(message);
      } else {
        Alert.alert("Save Complete", message);
      }

      if (savedCount > 0) {
        setCurrentStep('complete');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }

    } catch (error) {
      console.error('❌ Bulk save failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save opportunities';
      const fullMessage = `Save failed: ${errorMessage}`;
      
      if (Platform.OS === 'web') {
        alert(fullMessage);
      } else {
        Alert.alert("Save Error", fullMessage);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const toggleOpportunitySelection = (index: number) => {
    const newSelection = new Set(selectedOpportunities);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedOpportunities(newSelection);
  };

  const selectAll = () => {
    setSelectedOpportunities(new Set(parsedOpportunities.map((_, index) => index)));
  };

  const selectNone = () => {
    setSelectedOpportunities(new Set());
  };

  const loadTestContent = () => {
    setContent(getTestContent('simple'));
  };

  // Convert ParsedOpportunity to format expected by OrganizationOpportunityForm
  const convertParsedToFormData = (parsedOpp: ParsedOpportunity) => {
    return {
      title: parsedOpp.title,
      description: parsedOpp.description,
      type: Array.isArray(parsedOpp.type) ? parsedOpp.type : [parsedOpp.type],
      organization: parsedOpp.organization,
      location: parsedOpp.location,
      website: parsedOpp.website || '',
      eligibility: parsedOpp.eligibility || '',
      cost: parsedOpp.cost || '',
      tags: parsedOpp.tags || [],
      gradeLevel: parsedOpp.gradeLevel || [],
      interactionTypes: parsedOpp.interactionTypes || [],
      isYearlyRecurring: parsedOpp.isYearlyRecurring || false,
      startDate: parsedOpp.startDate ? parseDateString(parsedOpp.startDate) : undefined,
      endDate: parsedOpp.endDate ? parseDateString(parsedOpp.endDate) : undefined,
      applicationOpenDate: parsedOpp.applicationOpenDate ? parseDateString(parsedOpp.applicationOpenDate) : undefined,
      applicationCloseDate: parsedOpp.applicationCloseDate ? parseDateString(parsedOpp.applicationCloseDate) : undefined,
    };
  };

  // Parse date string in MM/DD/YYYY format to Date object
  const parseDateString = (dateString: string): Date | undefined => {
    if (!dateString) return undefined;
    
    try {
      const [month, day, year] = dateString.split('/');
      if (month && day && year) {
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    } catch (error) {
      console.warn('Failed to parse date:', dateString);
    }
    return undefined;
  };

  const handleEditOpportunity = (index: number) => {
    const opportunity = parsedOpportunities[index];
    const formData = convertParsedToFormData(opportunity);
    setEditingOpportunityData(formData);
    setEditingOpportunity(index);
    setShowOpportunityForm(true);
  };

  const handleOpportunityFormSuccess = () => {
    // Close the opportunity form and return to AI import modal
    setShowOpportunityForm(false);
    setEditingOpportunityData(null);
    setEditingOpportunity(null);
  };

  const handleCloseOpportunityForm = () => {
    setShowOpportunityForm(false);
    setEditingOpportunityData(null);
    setEditingOpportunity(null);
  };

  const resetModal = () => {
    setCurrentStep('setup');
    setContent('');
    setParsedOpportunities([]);
    setSelectedOpportunities(new Set());
    setProcessingStats(null);
    setEditingOpportunity(null);
  };

  const renderSetupStep = () => (
    <View style={styles.stepContainer}>
      {/* Import Type Selection */}
      <View style={[styles.section, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
        <Text style={[styles.sectionTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
          Import Type
        </Text>
        <Text style={[styles.helpText, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
          Choose whether you want to import a single opportunity or multiple opportunities from the content.
        </Text>
        
        <View style={styles.importTypeContainer}>
          <TouchableOpacity
            style={[
              styles.importTypeCard,
              { 
                backgroundColor: importType === 'single' ? (theme === "dark" ? "#34343B" : "#EEF2FF") : (theme === "dark" ? "#2A2A2E" : "#F9FAFB"),
                borderColor: importType === 'single' ? "#2563EB" : (theme === "dark" ? "#2A2A2E" : "#E5E7EB")
              }
            ]}
            onPress={() => setImportType('single')}
            activeOpacity={0.7}
          >
            <View style={[styles.importTypeIcon, { backgroundColor: importType === 'single' ? "#2563EB" : (theme === "dark" ? "#34343B" : "#F3F4F6") }]}>
              <Ionicons name="document-text" size={20} color={importType === 'single' ? "#FFFFFF" : (theme === "dark" ? "#C2C2C2" : "#6B7280")} />
            </View>
            <View style={styles.importTypeContent}>
              <Text style={[styles.importTypeTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
                Single Opportunity
              </Text>
              <Text style={[styles.importTypeDescription, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
                Extract one main opportunity
              </Text>
            </View>
            {importType === 'single' && (
              <Ionicons name="checkmark-circle" size={16} color="#2563EB" />
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.importTypeCard,
              { 
                backgroundColor: importType === 'multiple' ? (theme === "dark" ? "#34343B" : "#EEF2FF") : (theme === "dark" ? "#2A2A2E" : "#F9FAFB"),
                borderColor: importType === 'multiple' ? "#2563EB" : (theme === "dark" ? "#2A2A2E" : "#E5E7EB")
              }
            ]}
            onPress={() => setImportType('multiple')}
            activeOpacity={0.7}
          >
            <View style={[styles.importTypeIcon, { backgroundColor: importType === 'multiple' ? "#2563EB" : (theme === "dark" ? "#34343B" : "#F3F4F6") }]}>
              <Ionicons name="list" size={20} color={importType === 'multiple' ? "#FFFFFF" : (theme === "dark" ? "#C2C2C2" : "#6B7280")} />
            </View>
            <View style={styles.importTypeContent}>
              <Text style={[styles.importTypeTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
                Multiple Opportunities
              </Text>
              <Text style={[styles.importTypeDescription, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
                Extract all opportunities found
              </Text>
            </View>
            {importType === 'multiple' && (
              <Ionicons name="checkmark-circle" size={16} color="#2563EB" />
            )}
          </TouchableOpacity>
        </View>
      </View>

      {/* Content Input */}
      <View style={[styles.section, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            <Text style={[styles.sectionTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
              Website Content
            </Text>
            <Text style={[styles.helpText, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
              Paste the HTML content, text content, or any website information that contains opportunities you want to import.
            </Text>
          </View>
          
          <TouchableOpacity
            style={[styles.testButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F3F4F6", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
            onPress={loadTestContent}
            activeOpacity={0.7}
          >
            <Ionicons name="flask" size={14} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
            <Text style={[styles.testButtonText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
              Load Test Content
            </Text>
          </TouchableOpacity>
        </View>
        
        <TextInput
          style={[styles.textArea, { 
            backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", 
            borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB", 
            color: theme === "dark" ? "#FAFAFA" : "#111827" 
          }]}
          value={content}
          onChangeText={setContent}
          placeholder="Paste website content here... (HTML, text, or any content containing opportunities)"
          placeholderTextColor={theme === "dark" ? "#9CA3AF" : "#9CA3AF"}
          multiline
          numberOfLines={8}
          textAlignVertical="top"
        />
        
        <TouchableOpacity
          style={[styles.parseButton, isProcessing && styles.parseButtonDisabled]}
          onPress={handleParseContent}
          disabled={isProcessing}
          activeOpacity={0.7}
        >
          {isProcessing ? (
            <>
              <ActivityIndicator size="small" color="#FFFFFF" />
              <Text style={styles.parseButtonText}>Parsing with AI...</Text>
            </>
          ) : (
            <>
              <Ionicons name="sparkles" size={20} color="#FFFFFF" />
              <Text style={styles.parseButtonText}>Parse with AI</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderProcessingStep = () => (
    <View style={styles.stepContainer}>
      <View style={[styles.processingCard, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
        <View style={styles.processingIcon}>
          <ActivityIndicator size="large" color="#2563EB" />
        </View>
        <Text style={[styles.processingTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
          AI is analyzing your content...
        </Text>
        <Text style={[styles.processingSubtitle, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
          {importType === 'single' 
            ? 'Looking for the main opportunity in your content' 
            : 'Scanning for all opportunities in your content'
          }
        </Text>
        <View style={styles.processingSteps}>
          <View style={[styles.processingStep, { backgroundColor: theme === "dark" ? "#34343B" : "#F3F4F6" }]}>
            <Ionicons name="checkmark-circle" size={16} color="#16A34A" />
            <Text style={[styles.processingStepText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
              Content received
            </Text>
          </View>
          <View style={[styles.processingStep, { backgroundColor: theme === "dark" ? "#34343B" : "#F3F4F6" }]}>
            <ActivityIndicator size="small" color="#2563EB" />
            <Text style={[styles.processingStepText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
              AI analyzing content
            </Text>
          </View>
          <View style={[styles.processingStep, { backgroundColor: theme === "dark" ? "#2A2A2E" : "#F9FAFB" }]}>
            <Ionicons name="time" size={16} color={theme === "dark" ? "#9CA3AF" : "#9CA3AF"} />
            <Text style={[styles.processingStepText, { color: theme === "dark" ? "#9CA3AF" : "#9CA3AF" }]}>
              Extracting opportunities
            </Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderProcessingStats = () => {
    if (!processingStats) return null;

    return (
      <View style={[styles.statsInline, { backgroundColor: theme === "dark" ? "rgba(34, 197, 94, 0.1)" : "rgba(34, 197, 94, 0.08)" }]}>
        <Ionicons name="checkmark-circle" size={16} color="#16A34A" />
        <Text style={[styles.statsInlineText, { color: theme === "dark" ? "#86EFAC" : "#16A34A" }]}>
          <Text style={styles.statsInlineBold}>{parsedOpportunities.length}</Text> extracted
        </Text>
      </View>
    );
  };


  // Format date for display
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Not set';
    
    try {
      // Handle MM/DD/YYYY format from AI parsing
      const [month, day, year] = dateString.split('/');
      if (month && day && year) {
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
        }
      }
      return dateString; // Return original if parsing fails
    } catch (error) {
      return dateString || 'Invalid date';
    }
  };

  const renderOpportunityPreview = (opportunity: ParsedOpportunity, index: number) => {
    const isSelected = selectedOpportunities.has(index);
    const validation = validateOpportunity(opportunity);
    const hasErrors = !validation.valid;
    
    return (
      <TouchableOpacity
        key={index}
        style={[
          styles.opportunityCardCompact,
          { 
            backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", 
            borderColor: hasErrors ? "#DC2626" : isSelected ? "#2563EB" : (theme === "dark" ? "#2A2A2E" : "#E5E7EB"),
            borderWidth: isSelected ? 2 : 1,
            opacity: isSelected ? 1 : 0.85,
          }
        ]}
        onPress={() => toggleOpportunitySelection(index)}
        activeOpacity={0.7}
      >
        {/* Header with title and actions */}
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderLeft}>
            <View style={[styles.checkbox, { 
              backgroundColor: isSelected ? "#2563EB" : "transparent",
              borderColor: isSelected ? "#2563EB" : (theme === "dark" ? "#4B5563" : "#D1D5DB")
            }]}>
              {isSelected && <Ionicons name="checkmark" size={14} color="#FFFFFF" />}
            </View>
            
            <View style={styles.cardTitleSection}>
              <View style={styles.titleRow}>
                <Text style={[styles.cardTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]} numberOfLines={1}>
                  {opportunity.title || 'Untitled Opportunity'}
                </Text>
                {hasErrors && (
                  <View style={[styles.errorBadgeSmall, { backgroundColor: theme === "dark" ? "rgba(239, 68, 68, 0.2)" : "rgba(220, 38, 38, 0.1)" }]}>
                    <Ionicons name="alert-circle" size={10} color={theme === "dark" ? "#FCA5A5" : "#DC2626"} />
                    <Text style={[styles.errorBadgeSmallText, { color: theme === "dark" ? "#FCA5A5" : "#DC2626" }]}>
                      {validation.errors.length}
                    </Text>
                  </View>
                )}
              </View>
              <Text style={[styles.cardMeta, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]} numberOfLines={1}>
                {Array.isArray(opportunity.type) ? opportunity.type[0] : opportunity.type || 'No type'} • {opportunity.location || 'No location'}
              </Text>
            </View>
          </View>
          
          <TouchableOpacity
            style={[styles.editButtonCompact, { 
              backgroundColor: hasErrors 
                ? (theme === "dark" ? "rgba(220, 38, 38, 0.2)" : "rgba(220, 38, 38, 0.1)")
                : (theme === "dark" ? "#34343B" : "#F3F4F6")
            }]}
            onPress={(e) => {
              e.stopPropagation();
              handleEditOpportunity(index);
            }}
            activeOpacity={0.7}
          >
            <Ionicons 
              name={hasErrors ? "create" : "pencil-outline"} 
              size={16} 
              color={hasErrors ? (theme === "dark" ? "#FCA5A5" : "#DC2626") : (theme === "dark" ? "#9CA3AF" : "#6B7280")} 
            />
          </TouchableOpacity>
        </View>
        
        {/* Compact missing fields indicator */}
        {hasErrors && (
          <TouchableOpacity
            style={[styles.missingFieldsCompact, { backgroundColor: theme === "dark" ? "rgba(220, 38, 38, 0.15)" : "rgba(220, 38, 38, 0.08)" }]}
            onPress={(e) => {
              e.stopPropagation();
              handleEditOpportunity(index);
            }}
            activeOpacity={0.7}
          >
            <Ionicons name="warning-outline" size={12} color={theme === "dark" ? "#FCA5A5" : "#DC2626"} />
            <Text style={[styles.missingFieldsCompactText, { color: theme === "dark" ? "#FCA5A5" : "#DC2626" }]}>
              Missing: {validation.errors.join(', ')}
            </Text>
            <Ionicons name="chevron-forward" size={12} color={theme === "dark" ? "#FCA5A5" : "#DC2626"} />
          </TouchableOpacity>
        )}
        
        {/* Compact info grid */}
        <View style={styles.infoGrid}>
          {(opportunity.startDate || opportunity.applicationCloseDate) && (
            <View style={styles.infoItem}>
              <Ionicons name="calendar-outline" size={11} color={theme === "dark" ? "#6B7280" : "#9CA3AF"} />
              <Text style={[styles.infoText, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
                {opportunity.applicationCloseDate 
                  ? `Deadline: ${formatDate(opportunity.applicationCloseDate)}`
                  : formatDate(opportunity.startDate)}
              </Text>
            </View>
          )}
          
          {opportunity.gradeLevel && opportunity.gradeLevel.length > 0 && (
            <View style={styles.infoItem}>
              <Ionicons name="school-outline" size={11} color={theme === "dark" ? "#6B7280" : "#9CA3AF"} />
              <Text style={[styles.infoText, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]} numberOfLines={1}>
                {opportunity.gradeLevel.slice(0, 2).join(", ")}
                {opportunity.gradeLevel.length > 2 && ` +${opportunity.gradeLevel.length - 2}`}
              </Text>
            </View>
          )}
          
          {opportunity.cost && (
            <View style={styles.infoItem}>
              <Ionicons name="cash-outline" size={11} color={theme === "dark" ? "#6B7280" : "#9CA3AF"} />
              <Text style={[styles.infoText, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
                {opportunity.cost}
              </Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderReviewStep = () => {
    return (
      <View style={styles.stepContainer}>
        <View style={[styles.section, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
          <View style={styles.listHeader}>
            <View style={styles.listHeaderLeft}>
              <View style={styles.listHeaderTop}>
                <Text style={[styles.sectionTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
                  Review & Edit Opportunities
                </Text>
                {renderProcessingStats()}
              </View>
              <Text style={[styles.listSubtitle, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
                {selectedOpportunities.size} of {parsedOpportunities.length} selected
              </Text>
            </View>
          
          <View style={styles.selectionControls}>
            <TouchableOpacity
              style={[styles.selectionButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F3F4F6", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
              onPress={selectAll}
              activeOpacity={0.7}
            >
              <Text style={[styles.selectionButtonText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
                Select All
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.selectionButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F3F4F6", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
              onPress={selectNone}
              activeOpacity={0.7}
            >
              <Text style={[styles.selectionButtonText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
                Select None
              </Text>
            </TouchableOpacity>
          </View>
        </View>
        
        <ScrollView style={styles.opportunityList} showsVerticalScrollIndicator={false}>
          {parsedOpportunities.map((opportunity, index) => renderOpportunityPreview(opportunity, index))}
        </ScrollView>
        
        <View style={styles.reviewActions}>
          <TouchableOpacity
            style={[styles.backButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F3F4F6", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}
            onPress={() => setCurrentStep('setup')}
            activeOpacity={0.7}
          >
            <Ionicons name="arrow-back" size={20} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
            <Text style={[styles.backButtonText, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
              Back to Setup
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
            onPress={handleSaveOpportunities}
            disabled={isSaving || selectedOpportunities.size === 0}
            activeOpacity={0.7}
          >
            {isSaving ? (
              <>
                <ActivityIndicator size="small" color="#FFFFFF" />
                <Text style={styles.saveButtonText}>Saving {selectedOpportunities.size} opportunities...</Text>
              </>
            ) : (
              <>
                <Ionicons name="save" size={20} color="#FFFFFF" />
                <Text style={styles.saveButtonText}>
                  Save {selectedOpportunities.size} Selected
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
    );
  };

  const renderCompleteStep = () => (
    <View style={styles.stepContainer}>
      <View style={[styles.completeCard, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
        <View style={styles.completeIcon}>
          <Ionicons name="checkmark-circle" size={64} color="#16A34A" />
        </View>
        <Text style={[styles.completeTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
          Import Complete!
        </Text>
        <Text style={[styles.completeSubtitle, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
          {selectedOpportunities.size} opportunities have been successfully imported to your organization.
        </Text>
        <View style={styles.completeStats}>
          <View style={styles.completeStat}>
            <Text style={[styles.completeStatValue, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
              {selectedOpportunities.size}
            </Text>
            <Text style={[styles.completeStatLabel, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
              Opportunities Saved
            </Text>
          </View>
          <View style={styles.completeStat}>
            <Text style={[styles.completeStatValue, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
              {(processingStats?.processingTime || 0) / 1000}s
            </Text>
            <Text style={[styles.completeStatLabel, { color: theme === "dark" ? "#9CA3AF" : "#6B7280" }]}>
              Processing Time
            </Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderStepIndicator = () => {
    const steps = [
      { key: 'setup', label: 'Setup', icon: 'settings' },
      { key: 'processing', label: 'Processing', icon: 'sparkles' },
      { key: 'review', label: 'Review', icon: 'eye' },
      { key: 'complete', label: 'Complete', icon: 'checkmark' }
    ];

    return (
      <View style={[styles.stepIndicator, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderBottomColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
        {steps.map((step, index) => {
          const isActive = currentStep === step.key;
          const isCompleted = ['setup', 'processing', 'review', 'complete'].indexOf(currentStep) > index;
          
          return (
            <View key={step.key} style={styles.stepIndicatorItem}>
              <View style={[
                styles.stepIndicatorCircle,
                {
                  backgroundColor: isActive ? "#2563EB" : isCompleted ? "#16A34A" : (theme === "dark" ? "#34343B" : "#F3F4F6"),
                  borderColor: isActive ? "#2563EB" : isCompleted ? "#16A34A" : (theme === "dark" ? "#2A2A2E" : "#E5E7EB")
                }
              ]}>
                {isCompleted ? (
                  <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                ) : (
                  <Ionicons name={step.icon as any} size={16} color={isActive ? "#FFFFFF" : (theme === "dark" ? "#C2C2C2" : "#6B7280")} />
                )}
              </View>
              <Text style={[
                styles.stepIndicatorLabel,
                { color: isActive ? "#2563EB" : (theme === "dark" ? "#C2C2C2" : "#6B7280") }
              ]}>
                {step.label}
              </Text>
            </View>
          );
        })}
      </View>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'setup':
        return renderSetupStep();
      case 'processing':
        return renderProcessingStep();
      case 'review':
        return renderReviewStep();
      case 'complete':
        return renderCompleteStep();
      default:
        return renderSetupStep();
    }
  };

  return (
    <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onClose}>
      <TouchableOpacity style={[styles.modal, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF" }]} activeOpacity={1} onPress={(e) => e.stopPropagation()}>
        {/* Header */}
        <View style={[styles.header, { backgroundColor: theme === "dark" ? "#1F1F23" : "#FFFFFF", borderBottomColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]}>
          <View style={styles.headerLeft}>
            <TouchableOpacity style={[styles.closeButton, { backgroundColor: theme === "dark" ? "#34343B" : "#F9FAFB", borderColor: theme === "dark" ? "#2A2A2E" : "#E5E7EB" }]} onPress={onClose}>
              <Ionicons name="close" size={20} color={theme === "dark" ? "#C2C2C2" : "#6B7280"} />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={[styles.headerTitle, { color: theme === "dark" ? "#FAFAFA" : "#111827" }]}>
                AI Opportunity Import
              </Text>
              <Text style={[styles.headerSubtitle, { color: theme === "dark" ? "#C2C2C2" : "#6B7280" }]}>
                Import opportunities from website content using AI
              </Text>
            </View>
          </View>
        </View>

        {/* Step Indicator */}
        {renderStepIndicator()}

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          {renderCurrentStep()}
        </ScrollView>
      </TouchableOpacity>

      {/* Opportunity Form Modal */}
      {showOpportunityForm && (
        <OrganizationOpportunityForm
          opportunityId={null} // This is for editing AI-parsed data, not existing opportunities
          opportunityData={editingOpportunityData}
          onClose={handleCloseOpportunityForm}
          onSuccess={handleOpportunityFormSuccess}
          organizationUserId={organizationUserId}
        />
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    width: '95%',
    maxWidth: 1200,
    maxHeight: '95%',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 8,
  },
  
  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    borderWidth: 1,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  headerSubtitle: {
    fontSize: 14,
    fontFamily: "Inter-Regular",
    marginTop: 2,
  },

  // Content
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 12,
    paddingBottom: 12,
  },
  section: {
    marginBottom: 16,
    borderRadius: 10,
    padding: 16,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 14,
  },
  sectionHeaderLeft: {
    flex: 1,
    marginRight: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 4,
    fontFamily: "Inter-SemiBold",
    letterSpacing: -0.2,
  },
  helpText: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: "Inter-Regular",
  },
  testButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    gap: 6,
  },
  testButtonText: {
    fontSize: 12,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  textArea: {
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 14,
    marginBottom: 14,
    minHeight: 110,
    fontFamily: "Inter-Regular",
  },
  parseButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#2563EB",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  parseButtonDisabled: {
    backgroundColor: "#9CA3AF",
  },
  parseButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Stats - Inline compact version
  statsInline: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  statsInlineText: {
    fontSize: 13,
    fontFamily: "Inter-Medium",
  },
  statsInlineBold: {
    fontSize: 14,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
  },

  // Opportunity List
  listHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  listHeaderTop: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 6,
  },
  selectionControls: {
    flexDirection: "row",
    gap: 8,
  },
  selectionButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  selectionButtonText: {
    fontSize: 11,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  opportunityList: {
    maxHeight: 600,
    marginBottom: 14,
  },
  
  // New compact card design
  opportunityCardCompact: {
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    marginRight: 8,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  cardTitleSection: {
    flex: 1,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 3,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    flex: 1,
  },
  cardMeta: {
    fontSize: 12,
    fontFamily: "Inter-Regular",
  },
  errorBadgeSmall: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 3,
    gap: 3,
  },
  errorBadgeSmallText: {
    fontSize: 10,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
  },
  editButtonCompact: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  missingFieldsCompact: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 5,
    marginBottom: 8,
    gap: 6,
  },
  missingFieldsCompactText: {
    flex: 1,
    fontSize: 11,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  infoItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  infoText: {
    fontSize: 11,
    fontFamily: "Inter-Regular",
  },
  opportunityEligibility: {
    fontSize: 12,
    fontStyle: "italic",
    marginBottom: 4,
    fontFamily: "Inter-Regular",
  },
  opportunityCost: {
    fontSize: 12,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#2563EB",
    paddingHorizontal: 20,
    paddingVertical: 11,
    borderRadius: 8,
    gap: 6,
    shadowColor: "#2563EB",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 3,
  },
  saveButtonDisabled: {
    backgroundColor: "#9CA3AF",
    shadowOpacity: 0,
  },
  saveButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // New Step-based Styles
  stepContainer: {
    padding: 12,
  },
  
  // Step Indicator
  stepIndicator: {
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  stepIndicatorItem: {
    alignItems: "center",
    flex: 1,
  },
  stepIndicatorCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 6,
  },
  stepIndicatorLabel: {
    fontSize: 11,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Import Type Selection
  importTypeContainer: {
    flexDirection: "row",
    gap: 12,
  },
  importTypeCard: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
  },
  importTypeIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  importTypeContent: {
    flex: 1,
  },
  importTypeTitle: {
    fontSize: 15,
    fontWeight: "600",
    marginBottom: 3,
    fontFamily: "Inter-SemiBold",
  },
  importTypeDescription: {
    fontSize: 13,
    fontFamily: "Inter-Regular",
  },

  // Processing Step
  processingCard: {
    alignItems: "center",
    padding: 28,
    borderRadius: 12,
    borderWidth: 1,
  },
  processingIcon: {
    marginBottom: 18,
  },
  processingTitle: {
    fontSize: 21,
    fontWeight: "600",
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  processingSubtitle: {
    fontSize: 15,
    textAlign: "center",
    marginBottom: 24,
    fontFamily: "Inter-Regular",
  },
  processingSteps: {
    width: "100%",
    gap: 10,
  },
  processingStep: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 6,
    gap: 12,
  },
  processingStepText: {
    fontSize: 14,
    fontFamily: "Inter-Medium",
  },

  // Review Step
  listHeaderLeft: {
    flex: 1,
  },
  listSubtitle: {
    fontSize: 14,
    marginTop: 4,
    fontFamily: "Inter-Regular",
  },
  reviewActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
    gap: 12,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    gap: 6,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },



  // Complete Step
  completeCard: {
    alignItems: "center",
    padding: 40,
    borderRadius: 16,
    borderWidth: 1,
  },
  completeIcon: {
    marginBottom: 24,
  },
  completeTitle: {
    fontSize: 28,
    fontWeight: "700",
    marginBottom: 12,
    fontFamily: "Inter-Bold",
  },
  completeSubtitle: {
    fontSize: 16,
    textAlign: "center",
    marginBottom: 32,
    lineHeight: 24,
    fontFamily: "Inter-Regular",
  },
  completeStats: {
    flexDirection: "row",
    gap: 32,
  },
  completeStat: {
    alignItems: "center",
  },
  completeStatValue: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 4,
    fontFamily: "Inter-Bold",
  },
  completeStatLabel: {
    fontSize: 12,
    fontFamily: "Inter-Medium",
  },
});

export default AIImportModal;
